import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { MapPin, Search, Star, Fish, Filter, Calendar, Award, ChevronDown, X, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import FisheryImage from '../components/FisheryImage';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  'All Regions',
  'South East',
  'South West',
  'East Midlands',
  'West Midlands',
  'East of England',
  'Yorkshire & Humber',
  'North West',
  'Wales'
];

const Lakes = () => {
  const { token } = useAuth();
  const [lakes, setLakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fishFilter, setFishFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [showNamedFish, setShowNamedFish] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const fishTypes = ['Common Carp', 'Mirror Carp', 'Leather Carp', 'Ghost Carp', 'Koi Carp', 'Grass Carp'];

  useEffect(() => {
    fetchLakes();
  }, [search, fishFilter, regionFilter, showNamedFish]);

  const fetchLakes = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (fishFilter && fishFilter !== 'all') params.append('fish_type', fishFilter);
      if (regionFilter && regionFilter !== 'all' && regionFilter !== 'All Regions') {
        params.append('region', regionFilter);
      }
      if (showNamedFish) params.append('has_named_fish', 'true');

      const response = await axios.get(`${API}/lakes?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLakes(response.data);
    } catch (error) {
      console.error('Failed to fetch lakes:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setFishFilter('');
    setRegionFilter('');
    setShowNamedFish(false);
  };

  const activeFilterCount = [fishFilter, regionFilter, showNamedFish].filter(Boolean).length;

  return (
    <div className="min-h-screen" data-testid="lakes-page">
      {/* Hero */}
      <div 
        className="relative h-72 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1604227694902-8d79311c6f19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxtaXN0eSUyMGxha2UlMjBmaXNoaW5nJTIwZGF3biUyMGRhcmslMjBtb29keXxlbnwwfHx8fDE3NzExNjg0MDZ8MA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#050605] via-[#050605]/70 to-transparent" />
        <div className="relative z-10 h-full flex items-end pb-8 px-6 md:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <p className="data-label text-[#c8b273] mb-2">UK Lake Guide</p>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-extrabold uppercase tracking-wider text-white">
              Find Your Perfect Water
            </h1>
            <p className="text-[#8a948a] mt-2 max-w-xl">
              Discover {lakes.length}+ premier carp fisheries across the UK. Browse famous waters, legendary fish, and book your next session.
            </p>
          </div>
        </div>
      </div>

      {/* Search & Filters Bar */}
      <div className="sticky top-16 z-20 bg-[#050605]/95 backdrop-blur-sm border-b border-white/5 py-4 px-6 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4a504a]" />
              <Input
                placeholder="Search lakes, locations, counties..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-[#0b0d0b] border-white/10 text-white"
                data-testid="search-input"
              />
            </div>

            {/* Desktop Filters */}
            <div className="hidden md:flex gap-3">
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger className="w-44 bg-[#0b0d0b] border-white/10 text-white" data-testid="region-filter">
                  <MapPin className="w-4 h-4 mr-2 text-[#c8b273]" />
                  <SelectValue placeholder="Region" />
                </SelectTrigger>
                <SelectContent className="bg-[#0b0d0b] border-white/10">
                  <SelectItem value="all">All Regions</SelectItem>
                  {REGIONS.filter(r => r !== 'All Regions').map(region => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={fishFilter} onValueChange={setFishFilter}>
                <SelectTrigger className="w-40 bg-[#0b0d0b] border-white/10 text-white" data-testid="fish-filter">
                  <Fish className="w-4 h-4 mr-2 text-[#c8b273]" />
                  <SelectValue placeholder="Fish Type" />
                </SelectTrigger>
                <SelectContent className="bg-[#0b0d0b] border-white/10">
                  <SelectItem value="all">All Types</SelectItem>
                  {fishTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant={showNamedFish ? "default" : "outline"}
                onClick={() => setShowNamedFish(!showNamedFish)}
                className={showNamedFish ? "bg-[#c8b273] text-black hover:bg-[#b5a066]" : "border-white/10 text-white hover:bg-white/5"}
                data-testid="named-fish-filter"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Named Fish
              </Button>

              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-[#8a948a] hover:text-white"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>

            {/* Mobile Filter Button */}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="outline" className="border-white/10 text-white">
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                  {activeFilterCount > 0 && (
                    <Badge className="ml-2 bg-[#c8b273] text-black">{activeFilterCount}</Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="bg-[#0b0d0b] border-white/10">
                <SheetHeader>
                  <SheetTitle className="text-white">Filter Lakes</SheetTitle>
                </SheetHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm text-[#8a948a] mb-2 block">Region</label>
                    <Select value={regionFilter} onValueChange={setRegionFilter}>
                      <SelectTrigger className="w-full bg-[#141714] border-white/10 text-white">
                        <SelectValue placeholder="All Regions" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0b0d0b] border-white/10">
                        <SelectItem value="all">All Regions</SelectItem>
                        {REGIONS.filter(r => r !== 'All Regions').map(region => (
                          <SelectItem key={region} value={region}>{region}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-[#8a948a] mb-2 block">Fish Type</label>
                    <Select value={fishFilter} onValueChange={setFishFilter}>
                      <SelectTrigger className="w-full bg-[#141714] border-white/10 text-white">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0b0d0b] border-white/10">
                        <SelectItem value="all">All Types</SelectItem>
                        {fishTypes.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant={showNamedFish ? "default" : "outline"}
                    onClick={() => setShowNamedFish(!showNamedFish)}
                    className={`w-full ${showNamedFish ? "bg-[#c8b273] text-black" : "border-white/10 text-white"}`}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Show Only Lakes with Named Fish
                  </Button>
                  <div className="flex gap-2 pt-4">
                    <Button variant="outline" onClick={clearFilters} className="flex-1 border-white/10 text-white">
                      Clear All
                    </Button>
                    <Button onClick={() => setFiltersOpen(false)} className="flex-1 btn-gold">
                      Apply Filters
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Results count */}
          <div className="mt-3 text-sm text-[#8a948a]">
            Showing {lakes.length} {lakes.length === 1 ? 'fishery' : 'fisheries'}
            {(search || fishFilter || regionFilter || showNamedFish) && ' matching your filters'}
          </div>
        </div>
      </div>

      {/* Lakes Grid */}
      <div className="p-6 md:p-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="glass-card h-80 animate-pulse bg-[#141714]" />
              ))}
            </div>
          ) : lakes.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <MapPin className="w-16 h-16 text-[#4a504a] mx-auto mb-4" />
              <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-2">
                No Lakes Found
              </h3>
              <p className="text-[#8a948a] mb-4">
                {search || fishFilter || regionFilter ? 'Try adjusting your filters' : 'No lakes available'}
              </p>
              {(search || fishFilter || regionFilter || showNamedFish) && (
                <Button onClick={clearFilters} className="btn-gold">
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {lakes.map((lake, index) => (
                <motion.div
                  key={lake.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <Link to={`/lakes/${lake.id}`} data-testid={`lake-card-${lake.id}`}>
                    <div className="glass-card group overflow-hidden h-full hover:border-[#c8b273]/30 transition-all duration-300">
                      {/* Image */}
                      <FisheryImage
                        src={lake.image_url}
                        seed={lake.id || lake.name}
                        alt={lake.name}
                        aspect="none"
                        className="h-44"
                        imgClassName="group-hover:scale-105"
                        testId={`lake-card-image-${lake.id}`}
                      >
                        {/* Badges */}
                        <div className="absolute top-3 left-3 flex flex-wrap gap-2 z-10">
                          {lake.record_weight && (
                            <div className="bg-[#ff5e00] text-white text-xs font-bold uppercase tracking-wider px-2 py-1 rounded flex items-center gap-1">
                              <Award className="w-3 h-3" />
                              {lake.record_weight}lb
                            </div>
                          )}
                          {lake.named_fish?.length > 0 && (
                            <div className="bg-[#c8b273] text-black text-xs font-bold uppercase tracking-wider px-2 py-1 rounded flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              {lake.named_fish.length} Named
                            </div>
                          )}
                        </div>

                        {/* Price Tag */}
                        {lake.day_ticket_price && (
                          <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm text-white text-sm font-bold px-2 py-1 rounded z-10">
                            From £{lake.day_ticket_price}
                          </div>
                        )}
                      </FisheryImage>

                      {/* Content */}
                      <div className="p-4">
                        <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white group-hover:text-[#c8b273] transition-colors line-clamp-1">
                          {lake.name}
                        </h3>
                        <p className="text-[#8a948a] text-sm mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="line-clamp-1">{lake.location}, {lake.county}</span>
                        </p>
                        
                        {/* Stats Row */}
                        <div className="flex items-center gap-4 mt-3">
                          <span className="flex items-center gap-1 text-[#c8b273]">
                            <Star className="w-4 h-4 fill-current" />
                            <span className="text-sm">{lake.avg_rating?.toFixed(1) || '0.0'}</span>
                          </span>
                          <span className="flex items-center gap-1 text-[#8a948a]">
                            <Fish className="w-4 h-4" />
                            <span className="text-sm">{lake.catches_count || 0} catches</span>
                          </span>
                          {lake.lake_size_acres && (
                            <span className="text-[#8a948a] text-sm">
                              {lake.lake_size_acres} acres
                            </span>
                          )}
                        </div>

                        {/* Fish Types */}
                        {lake.fish_types?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {lake.fish_types.slice(0, 3).map(type => (
                              <span key={type} className="text-xs bg-[#141714] text-[#6b7d6b] px-2 py-1 rounded">
                                {type}
                              </span>
                            ))}
                            {lake.fish_types.length > 3 && (
                              <span className="text-xs bg-[#141714] text-[#6b7d6b] px-2 py-1 rounded">
                                +{lake.fish_types.length - 3}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Named Fish Preview */}
                        {lake.named_fish?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-white/5">
                            <p className="text-xs text-[#c8b273] uppercase tracking-wider mb-1">Famous Fish</p>
                            <p className="text-sm text-[#8a948a] line-clamp-1">
                              {lake.named_fish.slice(0, 2).map(f => `${f.name} (${f.weight}lb)`).join(', ')}
                              {lake.named_fish.length > 2 && ` +${lake.named_fish.length - 2} more`}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Lakes;
